/* eslint-disable @typescript-eslint/typedef */
import React, { useContext, useEffect, useState } from "react";
import { CommentActionProps, Dropdown } from "semantic-ui-react";
import Button from "@material-ui/core/Button";
import { Theme, makeStyles } from "@material-ui/core/styles";
import CallIcon from "@material-ui/icons/Call";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Avatar from "@material-ui/core/Avatar";
import Alert from "@material-ui/lab/Alert";
import AppContext from "../contexts/AppContextProvider";
import { getLogger } from "../services/LoggingService";
import { IPhoneDirItem } from "../interfaces";
import { CallContext } from "../contexts/CallContext";
import { ConnectDataContext } from "../contexts/DataContext";
import { TokenContext } from "../contexts/TokenContext";
import { debounce } from "lodash";
import { addExternalConnection, getAgentContacts, placeOutboundCall } from "../connectExtensions";
import RestApiContext from "../contexts/RestApiContext";

const logger = getLogger("PhoneDirectory");
interface IProps {
    phoneDirectory: IPhoneDirItem[];
}
interface IState {
    error: string;
    initializingCall: boolean;
    buttonState: {
        text: string;
    };
    agentUserName: string;
    dirChoice: {
        key: string;
        outboundNumber: string;
        transferNumber: string;
        name: string;
        ext: string;
    };
}

const customSearchFunction = (options: any) => {
    return options;
};
const useStyles = makeStyles((theme: Theme) => ({
    mt10: {
        marginTop: "10px",
    },
    errorText: {
        padding: "1em",
        width: "100%",
        margin: "auto",
        textAlign: "justify",
        wordBreak: "break-word",
        whiteSpace: "pre-line",
        overflowWrap: "break-word",
        "-ms-word-break": "break-word",
        "-ms-hyphens": "auto",
        hyphens: "auto",
    },
}));
export function PhoneDirectory(props: IProps) {
    const classes = useStyles();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [state, setState] = useState<IState>({
        error: "",
        initializingCall: false,
        buttonState: {
            text: "Call",
        },
        agentUserName: "",
        dirChoice: {
            key: "",
            outboundNumber: "",
            transferNumber: "",
            ext: "",
            name: "",
        },
    });

    const { setAlert } = useContext(AppContext);
    const { agent, contact, contactDict } = useContext(CallContext);
    const { token } = useContext(TokenContext);
    const { setDirectorySearchTerm, availableQueueEndpoints, availableAgentEndpoints } = useContext(ConnectDataContext);
    const api = useContext(RestApiContext);

    useEffect(() => {
        if (!agent) return;
        setState((prev) => ({ ...prev, agentUserName: agent.getConfiguration().username }));
    }, [agent]);

    useEffect(() => {
        setDirectorySearchTerm(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        if (!contact) return;
        contact.onConnecting(() => handleConnecting());
        contact.onConnected(() => handleConnected());
        contact.onEnded(() => handleEnded());
        contact.onError(() => handleOnError());
    }, [contact]);

    const getContacts = () => {
        return (props.phoneDirectory || []).map((item: IPhoneDirItem, index: number) => {
            let number = item.ani;
            if (item.ext) {
                number += `x${item.ext}`;
            }
            return {
                key: `phone-directory-${item.name}-${index + 1}`,
                text: number,
                ext: item.ext,
                number: item.ani,
                value: `phone-directory-${item.name}-${index + 1}`,
                name: item.name,
                content: (
                    <ListItem dense={true}>
                        <ListItemIcon>
                            <Avatar onClick={() => clickToCall(number)}>
                                <CallIcon color="primary" />
                            </Avatar>
                        </ListItemIcon>
                        <ListItemText primary={item.name} secondary={`${number}`} />
                    </ListItem>
                ),
            };
        });
    };

    const handleChange = (event: any, data: any): void => {
        const choice = data.options.find((o) => o.key === data.value);

        setState({
            ...state,
            dirChoice: {
                key: choice ? choice.value : "",
                outboundNumber: choice ? choice.text : "",
                transferNumber: choice ? choice.number : "",
                ext: choice ? choice.ext : "",
                name: choice ? choice.name : "",
            },
        });
    };

    const onCallFailure = (err: any): void => {
        logger.debug("Error", err);
        logger.error(err);
        err = JSON.parse(err);
        setState({
            ...state,
            error: err.message ? err.message : "Outbound call error.  Please try again",
            initializingCall: false,
        });
        setAlert("Call Failed", "error");
        setTimeout(() => {
            setState({
                ...state,
                error: "",
            });
        }, 3500);
    };
    const clearInputs = (): void => {
        setState({
            ...state,
            initializingCall: false,
            dirChoice: {
                key: "",
                outboundNumber: "",
                transferNumber: "",
                ext: "",
                name: "",
            },
        });
    };
    const onCallSuccess = async (): Promise<void> => {
        logger.debug("Call was successful");
        setState({
            ...state,
            initializingCall: false,
        });
    };
    const onTransferSuccess = async (contactId: string): Promise<void> => {
        logger.debug("Transfer Call was successful");
        const transferAttributes = {
            transferExtension: state.dirChoice.ext,
            transferNumber: state.dirChoice.transferNumber,
            transferName: state.dirChoice.name,
        };
        await api.updateContactAttributes(contactId, transferAttributes);
        setState({
            ...state,
            initializingCall: false,
        });
    };
    const onTransferFailure = async (err: any, contactId: string): Promise<void> => {
        logger.debug("Error", err);
        logger.error(err);
        err = JSON.parse(err);
        setState({
            ...state,
            error: err.message ? err.message : "Outbound call error.  Please try again",
            initializingCall: false,
        });
        setAlert("Call Failed", "error");
        setTimeout(() => {
            setState({
                ...state,
                error: "",
            });
        }, 3500);
        const transferAttributes = {
            transferExtension: state.dirChoice.ext,
            transferNumber: state.dirChoice.transferNumber,
            transferName: state.dirChoice.name,
            transferErrorMessage: err.message ? err.message : "Transfer Error",
            transferErrorType: err.type,
        };
        await api.updateContactAttributes(contactId, transferAttributes);
    };
    const handleConnecting = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Connecting call ...",
            },
        });
    };
    const handleConnected = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Call",
            },
        });
    };
    const handleEnded = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Call",
            },
        });
        clearInputs();
    };
    const handleOnError = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Error calling",
            },
            error: "Outbound call error",
        });
        setTimeout(() => {
            setState({
                ...state,
                buttonState: {
                    ...state.buttonState,
                    text: "Call",
                },
                error: "",
            });
        }, 5000);
    };

    const clickToCall = async (phoneNumber: string) => {
        if (!agent) return;
        const parts = phoneNumber.split("x");
        const number = parts[0];
        const extension = parts.length === 2 ? parts[1] : "";

        const agentContacts = getAgentContacts(agent);

        setState({
            ...state,
            initializingCall: true,
        });
        try {
            if (agentContacts.length === 0) {
                logger.debug("no active voice or qcb contacts");
                await placeOutboundCall(agent, number, undefined, extension);
                onCallSuccess();
            } else if (agentContacts.length === 1) {
                logger.debug("one active voice or qcb contact");
                try {
                    await addExternalConnection(agentContacts[0], number, extension);
                    onTransferSuccess(agentContacts[0].contactId);
                } catch (err) {
                    logger.debug(err);
                    onTransferFailure(err, agentContacts[0].contactId);
                }
            } else {
                throw new Error("Already have 1 active voice contacts");
            }
        } catch (error) {
            onCallFailure(error);
        }
    };

    const handleQCTransfer = async (
        outboundPhoneNumber: string,
        transferName: string,
        quickConnect: connect.Endpoint
    ) => {
        if (!agent) return;
        const parts = outboundPhoneNumber.split("x");
        const number = parts[0];
        const extension = parts.length === 2 ? parts[1] : "";
        const agentContacts = getAgentContacts(agent);

        setState({
            ...state,
            initializingCall: true,
        });
        let requestOptions = {
            method: "POST",
            body: JSON.stringify({
                transferNumber: number,
                transferName: transferName,
                transferExtension: extension,
                call_id: agentContacts[0].contactId,
            }),
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            let response = await fetch("https://api.patientsync.us/prod/anitransfer", requestOptions);
            if (response.status === 200) {
                agentContacts[0].addConnection(quickConnect, {
                    success: () => onTransferSuccess(agentContacts[0].contactId),
                    failure: (err) => {
                        onTransferFailure(err, agentContacts[0].contactId);
                    },
                });
            } else {
                throw new Error("Error updating ani on transfer");
            }
        } catch (error) {
            onCallFailure(error);
        }
    };

    const handleCallClickRouter = (outboundPhoneNumber: string, transferName: string) => {
        const agentContacts = getAgentContacts(agent);
        const quickConnect = availableQueueEndpoints.find((val) => val.name === "aniTransfer");
        let attributeFlag = true;
        // if (contactDict[agentContacts[0].contactId].attributes?.transferCustomerANI?.value === "false") {
        //     attributeFlag = false;
        // }
        //get call attribute here
        if (agentContacts.length === 1 && attributeFlag && transferName && quickConnect) {
            handleQCTransfer(outboundPhoneNumber, transferName, quickConnect);
        } else {
            clickToCall(outboundPhoneNumber);
        }
    };

    const getNoResultsMessage = () => {
        if (props.phoneDirectory.length === 0 && !searchQuery) {
            return "Please enter a search term...";
        } else if (props.phoneDirectory.length === 0 && searchQuery) {
            return "Search returned no results, try again!";
        } else {
            return "No results!";
        }
    };
    const handleSearchChange = debounce((event: any, data: any) => {
        setSearchQuery(data.searchQuery);
    }, 350);

    const callError = state.error ? (
        <Alert className={classes.errorText} severity={"error"}>
            <div>{state.error}</div>
        </Alert>
    ) : null;

    return (
        <div id="outbound-call-options">
            <Dropdown
                clearable
                search={customSearchFunction}
                noResultsMessage={getNoResultsMessage()}
                selection
                fluid
                onChange={handleChange}
                onSearchChange={handleSearchChange}
                value={state.dirChoice.key}
                options={getContacts()}
                placeholder="Select number or click icon to call"
            />
            <Button
                // disabled={connected || connecting || incoming}
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                className={classes.mt10}
                endIcon={<CallIcon />}
                onClick={() => handleCallClickRouter(state.dirChoice.outboundNumber, state.dirChoice.name)}
            >
                {state.buttonState.text}
            </Button>
            <div style={{ width: "100%", marginTop: "10px" }}>{callError}</div>
        </div>
    );
}
